import type { DatevExport, Pagination } from "@/apis/gql/types";
import { useTenant } from "@/providers/session";
import { type ApolloQueryResult, gql, useQuery } from "@apollo/client";
import { useEffect, useMemo, useState } from "react";
import { useSpinDelay } from "spin-delay";

export type GetAllDatevExportsData = DatevExport[];

export type GetAllDatevExportsQuery = {
  datevExports: {
    data: DatevExport[];
    pagination: Pagination;
  };
};

export type UseGetAllDatevExportsParams = {
  page?: number;
  limit?: number;
  entityType: "invoice" | "bank-transaction" | "supplier";
  exportId?: string;
};

export type UseGetAllDatevExportsReturn = {
  data: GetAllDatevExportsData;
  loading: boolean;
  error: any;
  pagination: Pagination;
  refetch: (
    variables?: Partial<UseGetAllDatevExportsParams> | undefined,
  ) => Promise<ApolloQueryResult<GetAllDatevExportsQuery>>;
};

export const useGetAllDatevExports = ({
  page = 1,
  limit = 50,
  entityType,
  exportId,
}: UseGetAllDatevExportsParams): UseGetAllDatevExportsReturn => {
  const { tenantId } = useTenant();

  const [pagination, setPagination] = useState<Pagination>({
    currentPage: page,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: limit,
    hasNextPage: false,
    hasPreviousPage: false,
    nextPage: page,
    previousPage: page > 1 ? page - 1 : 1,
  });

  const { data, loading, error, refetch } = useQuery<GetAllDatevExportsQuery>(
    GET_ALL_DATEV_EXPORTS_QUERY,
    {
      variables: {
        tenantId,
        page,
        limit,
        entityType,
        exportId,
      },
      notifyOnNetworkStatusChange: false,
      fetchPolicy: "cache-and-network",
    },
  );

  useEffect(() => {
    if (data?.datevExports?.pagination && !loading) {
      setPagination(data.datevExports.pagination);
    }
  }, [data?.datevExports?.pagination, loading]);

  const exports = useMemo(() => {
    return data?.datevExports?.data ?? [];
  }, [data]);

  const isLoading = useSpinDelay(loading, { delay: 600, minDuration: 400 });

  return {
    data: exports,
    pagination,
    loading: isLoading,
    refetch,
    error,
  };
};

export const GET_ALL_DATEV_EXPORTS_QUERY = gql`
  query DatevExports(
    $tenantId: String!
    $page: Int
    $limit: Int
    $entityType: String
    $exportId: String
  ) {
    datevExports(
      tenantId: $tenantId
      page: $page
      limit: $limit
      entityType: $entityType
      exportId: $exportId
    ) {
      data {
        datevExportId
        state
        exportType
        exportedAt
        stackName
        entityCounts {
          total
          invoices
          bankTransactions
          suppliers
          uploaded
        }
        entityExports {
          entityId
          entityType
          uploaded
          entity {
            ... on Invoice {
              invoiceId
              invoiceNo
              totalAmount
            }
            ... on Transaction {
              bankTxnId
              amount
              counterpartyName
            }
            ... on Supplier {
              supplierId
              supplierName
            }
          }
        }
      }
      pagination {
        currentPage
        totalPages
        totalItems
        hasNextPage
      }
    }
  }
`;
